import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { chatbotAdminApi } from '../../../services/api'
import Card from '../../../components/ui/Card'
import LoadingSpinner from '../../../components/ui/LoadingSpinner'
import { formatDateTime } from '../../../utils/date'

export default function AdminAiAnalytics() {
  const { t } = useTranslation()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    chatbotAdminApi
      .analytics()
      .then(({ data: response }) => setData(response.data || response))
      .catch(() => setData(null))
      .finally(() => setLoading(false))
  }, [])

  if (loading) return <LoadingSpinner className="py-20" />
  if (!data) return <p className="text-sm text-text/60">{t('admin.aiAssistant.analyticsError')}</p>

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card className="p-5">
          <p className="text-sm text-text/60">{t('admin.aiAssistant.totalConversations')}</p>
          <p className="mt-1 text-3xl font-bold text-primary">{data.total_conversations}</p>
        </Card>
        <Card className="p-5">
          <p className="text-sm text-text/60">{t('admin.aiAssistant.avgLatency')}</p>
          <p className="mt-1 text-3xl font-bold text-primary">{data.average_latency_ms || 0} ms</p>
        </Card>
        <Card className="p-5">
          <p className="text-sm text-text/60">{t('admin.aiAssistant.errors')}</p>
          <p className="mt-1 text-3xl font-bold text-accent">{data.error_conversations}</p>
        </Card>
        <Card className="p-5">
          <p className="text-sm text-text/60">{t('admin.aiAssistant.model')}</p>
          <p className="mt-1 text-sm font-semibold text-primary">{data.model_name}</p>
          <p className="mt-1 text-xs text-text/50">
            {data.model_configured ? t('admin.aiAssistant.configured') : t('admin.aiAssistant.notConfigured')}
          </p>
        </Card>
      </div>

      <Card className="p-5">
        <h2 className="mb-3 text-lg font-semibold text-primary">{t('admin.aiAssistant.byLanguage')}</h2>
        <p className="text-sm text-text/70">EN: {data.by_language?.en || 0}</p>
        <p className="text-sm text-text/70">AR: {data.by_language?.ar || 0}</p>
      </Card>

      <Card padding={false} className="overflow-x-auto">
        <div className="border-b border-border px-4 py-3">
          <h2 className="text-lg font-semibold text-primary">{t('admin.aiAssistant.unanswered')}</h2>
          <p className="text-sm text-text/60">{t('admin.aiAssistant.unansweredHint')}</p>
        </div>
        <table className="min-w-full text-sm">
          <thead className="bg-primary/5">
            <tr className="text-start">
              <th className="px-4 py-3">{t('admin.aiAssistant.question')}</th>
              <th className="px-4 py-3">{t('admin.aiAssistant.when')}</th>
            </tr>
          </thead>
          <tbody>
            {(data.unanswered_samples || []).map((item) => (
              <tr key={item.id} className="border-b border-border/60">
                <td className="px-4 py-3">{item.content}</td>
                <td className="px-4 py-3 whitespace-nowrap">{formatDateTime(item.created_at)}</td>
              </tr>
            ))}
            {(data.unanswered_samples || []).length === 0 ? (
              <tr>
                <td className="px-4 py-6 text-text/50" colSpan={2}>
                  {t('admin.aiAssistant.noUnanswered')}
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </Card>
    </div>
  )
}
